import { selectRouterQueryParameters } from '@noths/polaris-client-next-redux';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { BrowseDataAPIFilterUID } from 'src/services/browse-data-api/constants/BrowseDataAPIFilterUID';
import { selectPathname } from '../../navigation/selectors';
import { getNextPageNumberNotLoaded, getPreviousPageNumberNotLoaded } from '../utils/pageNumbers';
import { selectFiltersWithActiveOptions } from './selectors';

export const selectFiltersAsUrlQuery = (state: ReduxApplicationState): URLSearchParams => {
  const facetsWithActiveOptions = selectFiltersWithActiveOptions(state);

  return facetsWithActiveOptions.reduce((urlParams, facet) => {
    // Don't include mainland UK delivery zone as that is the default
    if (facet.key === BrowseDataAPIFilterUID.DeliveryZones) {
      const activeDeliveryZone = facet.options.find((option) => option.active);

      if (activeDeliveryZone?.value === '1') {
        return urlParams;
      }
    }

    if (facet.key === BrowseDataAPIFilterUID.Price) {
      const activePriceOption = facet.options.find((option) => option.active);

      if (activePriceOption?.minValue) {
        urlParams.set('filter[price_at_least]', activePriceOption.minValue.toString());
      }
      if (activePriceOption?.maxValue) {
        urlParams.set('filter[price_at_most]', activePriceOption.maxValue.toString());
      }

      return urlParams;
    }

    const activeFacetOptions = facet.options.filter((option) => option.active);

    activeFacetOptions.forEach((option) => {
      if (option.value) {
        urlParams.append(facet.key, option.value.toString());
      }
    });

    return urlParams;
  }, new URLSearchParams());
};

export const selectProductsQueryAsUrlQuery = (
  state: ReduxApplicationState,
  page?: number,
): URLSearchParams => {
  const {
    productsQuery: { canonicalFilters, sortOptions = [] },
  } = state;

  const urlParams = selectFiltersAsUrlQuery(state);

  const { previewing } = selectRouterQueryParameters(state);

  if (previewing) {
    urlParams.set('previewing', 'true');
  }

  const activeSortOption = sortOptions.find((option) => option.active);

  if (activeSortOption) {
    urlParams.set('sort', activeSortOption.id);
  }

  // don't include page=1 as that is the default
  if (page && page > 1) {
    urlParams.set('page', page.toString());
  }

  if (canonicalFilters?.searchTerm) {
    urlParams.set('term', canonicalFilters.searchTerm);
  }

  return urlParams;
};

export const selectPreviousPageURL = (state: ReduxApplicationState): string | null => {
  const previousPageNumber = getPreviousPageNumberNotLoaded(state.products.pages);

  if (previousPageNumber === null) {
    return null;
  }

  const activeSortAndFilterURLParams = selectProductsQueryAsUrlQuery(state, previousPageNumber);
  const pathname = selectPathname(state);
  const queryString = activeSortAndFilterURLParams.toString();

  return queryString.length > 0 ? `${pathname}?${queryString}` : pathname;
};

export const selectNextPageURL = (state: ReduxApplicationState): string | null => {
  const nextPageNumber = getNextPageNumberNotLoaded(
    state.products.pages,
    state.products.totalPages,
  );

  if (nextPageNumber === null) {
    return null;
  }

  const activeSortAndFilterURLParams = selectProductsQueryAsUrlQuery(state, nextPageNumber);
  const pathname = selectPathname(state);
  const queryString = activeSortAndFilterURLParams.toString();

  return queryString.length > 0 ? `${pathname}?${queryString}` : pathname;
};
